import { Back, Sine, TimelineLite, TweenMax } from "gsap";
import * as HLF from "html-living-framework";
import { Emitter } from "pixi-particles";
import { Application } from "../Application";
import * as Constants from "../Data/Constants";
import * as SFX from "../Data/SoundEffectKeys";
import * as VoiceOver from "../Data/VoiceOverKeys";
import { Button } from "../GameObjects/Button";
import { Fabuloso } from "../GameObjects/Fabuloso";
import { KeyboardFocusManager } from "../GameObjects/KeyboardFocusManager";
import { InteruptType, VoiceCategory } from "../GameObjects/VoiceOverManager";
import { ResultType } from "../States/Results";

interface ICharacterData {
    asset: string;
    sheet: string;
    frames: number;
    pos: PIXI.Point;
}

export class Circus extends HLF.State {
    private static readonly CONFETTI_INTERVAL: PIXI.Point = new PIXI.Point(0.15, 0.35);
    private static readonly CHARACTER_DATA: ICharacterData[] = [
        {
            asset: "andy-wave_",
            sheet: Constants.SPRITESHEET_CIRCUS_PREFIX + "01",
            frames: 24,
            pos: new PIXI.Point(-160, 170),
        },
        {
            asset: "carmen-wave_",
            sheet: Constants.SPRITESHEET_CIRCUS_PREFIX + "01",
            frames: 22,
            pos: new PIXI.Point(-80, 170),
        },
        {
            asset: "leo-wave_",
            sheet: Constants.SPRITESHEET_CIRCUS_PREFIX + "01",
            frames: 21,
            pos: new PIXI.Point(0, 170),
        },
        {
            asset: "luna-wave_",
            sheet: Constants.SPRITESHEET_CIRCUS_PREFIX + "02",
            frames: 28,
            pos: new PIXI.Point(130, 170),
        },
    ];
    private static readonly ANIM_KEY: string = "AnimKey";

    private _bg: PIXI.Sprite;
    private _fabuloso: Fabuloso;
    private _characters: HLF.AnimatedSprite[];
    private _button: Button;
    private _overlay: PIXI.Sprite;
    private _confettiAssets: any[];
    private _activeConfetti: Emitter[];
    private _confettiPool: Emitter[];
    private _confettiContainer: PIXI.Container;
    private _confettiSpawnRect: PIXI.Rectangle;
    private _keyboardFocusManager: KeyboardFocusManager;
    private _balloons: PIXI.Sprite[];

    private _timeline: TimelineLite | undefined;
    // private _confettiCompleteTimers: TimelineLite[];
    private _confettiSpawnTimer: TimelineLite | undefined;
    private _hoverTweens: TweenMax[];

    constructor() {
        super();

        this._bg = HLF.PixiUtils.makeSprite("circus-interior");
        this.addChild(this._bg);

        this._characters = new Array<HLF.AnimatedSprite>(Circus.CHARACTER_DATA.length);
        for (let i = 0; i < this._characters.length; ++i) {
            const data: ICharacterData = Circus.CHARACTER_DATA[i];
            this._characters[i] = new HLF.AnimatedSprite();
            this._characters[i].addAnimation(Circus.ANIM_KEY, new HLF.SpriteAnimation(
                data.asset, data.sheet, data.frames, 2,
                20, true,
            ));
            this._characters[i].play(Circus.ANIM_KEY);
            this._characters[i].position.copy(data.pos);
            this._characters[i].anchor.set(0.5, 1.0);
            this.addChild(this._characters[i]);
        }

        this._fabuloso = new Fabuloso();
        this._fabuloso.scale.x = this._fabuloso.scale.x * -1;
        this.addChild(this._fabuloso);

        this._balloons = new Array<PIXI.Sprite>(8);
        for (let i = 0; i < this._balloons.length; ++i) {
            this._balloons[i] = HLF.PixiUtils.makeSprite("balloon-0" + i, Constants.SPRITESHEET_GAMEPLAY);
            this.addChild(this._balloons[i]);
        }
        this._hoverTweens = new Array<TweenMax>(this._balloons.length);

        this._confettiAssets = new Array<any>();
        const shapes: string[] = [ "rect", "circle", "tri" ];
        const sheet: PIXI.loaders.Resource = PIXI.loader.resources[Constants.SPRITESHEET_UI];
        for (let i = 0; i < 4; ++i) {
            for (let j = 0; j < shapes.length; ++j) {
                this._confettiAssets.push(sheet.textures!["particle-confetti-" + shapes[j] + "-0" + i]);
            }
        }

        this._confettiContainer = new PIXI.Container();
        this.addChild(this._confettiContainer);

        this._activeConfetti = new Array<Emitter>();
        this._confettiPool = new Array<Emitter>();

        this._confettiSpawnRect = new PIXI.Rectangle();

        this._button = new Button(
            this.onButtonContinuePressed.bind(this),
            "button-play", Constants.SPRITESHEET_UI,
            VoiceOver.PLAY,
            SFX.UI_SELECT,
        );
        this._button.autoPulse = true;
        this._button.position.set(0, 300);
        this.addChild(this._button);

        this._keyboardFocusManager = new KeyboardFocusManager();
        this.addChild(this._keyboardFocusManager);

        this._overlay = HLF.PixiUtils.makeSprite("white-pixel", Constants.SPRITESHEET_UI);
        this._overlay.tint = 0x000000;
        this.addChild(this._overlay);

        PubSub.publishSync(HLF.PUSH_KEYBOARD_LAYER, undefined);

        Application.instance.voiceOver.play(
            VoiceOver.CONGRATULATIONS,
            InteruptType.EQUAL,
            VoiceCategory.INSTRUCTIONAL,
        );
    }

    public static getRequiredAssets(): string[] {
        const requiredAssets: string[] = [
            "circus-interior" + HLF.ResourceUtils.FILE_EXTENSION_JPG,
            Constants.SPRITESHEET_CIRCUS_PREFIX + "01"  + HLF.ResourceUtils.FILE_EXTENSION_JSON,
            Constants.SPRITESHEET_CIRCUS_PREFIX + "02"  + HLF.ResourceUtils.FILE_EXTENSION_JSON,
            Constants.SPRITESHEET_GAMEPLAY + HLF.ResourceUtils.FILE_EXTENSION_JSON,
        ];
        return requiredAssets;
    }

    public update(pDeltaTime: number): void {
        this._fabuloso.update(pDeltaTime);
        for (let i = 0; i < this._activeConfetti.length; ++i) {
            this._activeConfetti[i].update(pDeltaTime);
        }
        for (let i = 0; i < this._characters.length; ++i) {
            this._characters[i].update(pDeltaTime);
        }
    }

    public onResize(pSize: PIXI.Point): void {
        super.onResize(pSize);

        this._fabuloso.position.set(
            -this._size.x * 0.5 + 150,
            this._size.y * 0.5,
        );
        this._overlay.scale.set(this._size.x, this._size.y);
        this._confettiSpawnRect.x = -this._size.x * 0.5;
        this._confettiSpawnRect.width = this._size.x;
        this._confettiSpawnRect.y = -this._size.y * 0.6;
        this._confettiSpawnRect.height = this._size.y * 0.3;
    }

    public animateIn(pOnComplete: () => void): void {
        pOnComplete();

        this._fabuloso.playResultsAnim(ResultType.COMPLETE);
        this.scale.set(1.1);
        this._button.position.y = 600;
        const buffer: number = 105;
        const start: number = -this._size.x * 0.5 + buffer;
        const delta: number = (this._size.x - 2 * buffer) / (this._balloons.length - 1);
        const scale: number = Math.min(1, delta / this._balloons[0].width);
        const interval: number = 0.25;
        for (let i = 0; i < this._balloons.length; ++i) {
            this._balloons[i].position.set(start + delta * i, -this._size.y * 0.7);
            this._balloons[i].scale.set(scale);
        }

        Application.instance.audio.play(SFX.CROWD_START, 1, false, HLF.AudioCategory.SFX);
        if (this._timeline !== undefined) { this._timeline.kill(); }
        this._timeline = new TimelineLite();

        this._timeline.to(this.scale, 1.5, {
            x: 1,
            y: 1,
            ease: Sine.easeInOut,
        }, 0);
        this._timeline.to(this._overlay, 0.75, {
            alpha: 0,
            ease: Sine.easeInOut,
        }, 0);
        for (let i = 0; i < this._balloons.length; ++i) {
            const index: number = i;
            this._timeline.to(this._balloons[i], 2.0, {
                y: -200,
                ease: Back.easeOut,
            }, 0 + 0.165 * i);
            this._timeline.call(() => {
                this.playConfetti(new PIXI.Point(this._balloons[index].x, -200));
            }, undefined, this, 0.5 + interval * i);
            this._timeline.call(() => {
                this.startHover(index);
            }, undefined, this, 2.0 + interval * i);
        }
        this._timeline.call(this.confettiLoop.bind(this), undefined, this, 2.25);
        this._timeline.call(() => {
            Application.instance.audio.play(SFX.CROWD_LOOP, 1, true, HLF.AudioCategory.SFX);
        }, undefined, this, 3.0);
        this._timeline.to(this._button, 0.75, {
            y: 280,
            ease: Back.easeOut,
        }, 3.5);
        this._timeline.call(() => {
            PubSub.publishSync(HLF.PUSH_KEYBOARD_LAYER, undefined);
            PubSub.publishSync(HLF.REGISTER_FOCUSABLE, this._button);
        }, undefined, this, 4.25);
        this._timeline.call(() => {
            this._button.playPulse();
        }, undefined, this, 4.0);
    }

    public animateOut(pOnComplete: () => void): void {
        if (this._timeline !== undefined) { this._timeline.kill(); }

        const sfx: HLF.HowlerTrack = Application.instance.audio.getAudioTrack(
            SFX.CROWD_LOOP, HLF.AudioCategory.SFX,
        )! as HLF.HowlerTrack;
        sfx.getSource().fade(sfx.getSource().volume(), 0, 500);

        this._timeline = new TimelineLite();
        this._timeline.to(this.scale, 1.0, {
            x: 1.05,
            y: 1.05,
            ease: Sine.easeInOut,
        }, 0);
        this._timeline.to(this._overlay, 0.75, {
            alpha: 1,
            ease: Sine.easeInOut,
        }, 0.25);
        this._timeline.call(pOnComplete);
    }

    public destroy(pOptions: boolean | PIXI.DestroyOptions = { children: true }): void {
        if (this._timeline !== undefined) { this._timeline.kill(); }
        if (this._confettiSpawnTimer !== undefined) { this._confettiSpawnTimer.kill(); }
        for (let i = 0; i < this._activeConfetti.length; ++i) {
            this._activeConfetti[i].cleanup();
        }
        for (let i = 0; i < this._confettiPool.length; ++i) {
            this._confettiPool[i].cleanup();
        }
        for (let i = 0; i < this._hoverTweens.length; ++i) {
            if (this._hoverTweens[i] !== undefined) { this._hoverTweens[i].kill(); }
        }
        Application.instance.audio.getAudioTrack(SFX.CROWD_LOOP, HLF.AudioCategory.SFX)!.stop();
        // for (let i = 0; i < this._confettiCompleteTimers.length; ++i) {
        //     this._confettiCompleteTimers[i].kill();
        // }
        super.destroy(pOptions);
    }

    private onButtonContinuePressed(): void {
        this._button.interactive = false;
        PubSub.publishSync(HLF.POP_KEYBOARD_LAYER, undefined);
        PubSub.publishSync(HLF.STATE_TRANSITION, new HLF.StateToken(
            Constants.STATE_SELECTION,
            Constants.LOAD_INTERSTITIAL,
        ));
    }

    private confettiLoop(): void {
        this.playConfetti();
        if (this._confettiSpawnTimer !== undefined) { this._confettiSpawnTimer.kill(); }
        this._confettiSpawnTimer = new TimelineLite();
        this._confettiSpawnTimer.call(() => {
            this.confettiLoop();
        }, undefined, this, HLF.Random.floatBetweenPoint(Circus.CONFETTI_INTERVAL));
    }

    private playConfetti(pPos?: PIXI.Point): void {
        const confetti: Emitter = this.getConfetti();
        if (pPos !== undefined) {
            confetti.updateSpawnPos(
                pPos.x,
                pPos.y,
            );
        }
        else {
            confetti.updateSpawnPos(
                HLF.Random.floatBetween(this._confettiSpawnRect.left, this._confettiSpawnRect.right),
                HLF.Random.floatBetween(this._confettiSpawnRect.bottom, this._confettiSpawnRect.top),
            );
        }
        this._activeConfetti.push(confetti);
        confetti.playOnce(() => {
            // confetti.updateSpawnPos(0, 0);
            this._activeConfetti.splice(this._activeConfetti.indexOf(confetti), 1);
            this._confettiPool.push(confetti);
        });
    }

    private getConfetti(): Emitter {
        if (this._confettiPool.length === 0) {
            this.makeConfetti();
        }
        else {
            this._confettiPool[this._confettiPool.length - 1].resetPositionTracking();
        }
        return this._confettiPool.pop()!;
    }

    private makeConfetti(): void {
        const confetti: Emitter = new Emitter(
            this._confettiContainer,
            this._confettiAssets,
            {
                alpha: {
                    list: [
                        { value: 0.1, time: 0 },
                        { value: 0.8, time: 0.1 },
                        { value: 0.8, time: 0.8 },
                        { value: 0.1, time: 1 },
                    ],
                    isStepped: false,
                },
                speed: {
                    list: [
                        { value: 300, time: 0 },
                    ],
                    isStepped: false,
                    minimumSpeedMultiplier: 0.1,
                },
                acceleration: new PIXI.Point(0, 300),
                startRotation: { min: 135, max: 405 },
                rotationSpeed: { min: 0, max: 0 },
                lifetime: { min: 1.0, max: 1.0 },
                frequency: 0.01,
                spawnChance: 1,
                particlesPerWave: 50,
                emitterLifetime: 0.75,
                maxParticles: 50,
                pos: { x: 0, y: 0 },
                addAtBack: false,
                spawnType: "circle",
                spawnCircle: { x: 0, y: 0, r: 40 },
            },
        );
        this._confettiPool.push(confetti);
    }

    private startHover(pIndex: number): void {
        this._hoverTweens[pIndex] = TweenMax.to(this._balloons[pIndex], HLF.Random.floatBetween(3.0, 4.0), {
            y: -215,
            ease: Sine.easeInOut,
            repeat: -1,
            yoyo: true,
        });
    }
}
